import type { OnChangeFn, Row, RowSelectionState } from "@tanstack/react-table";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useRef,
} from "react";

interface ModifierFlags {
	ctrlKey: boolean;
	metaKey: boolean;
	shiftKey: boolean;
}

interface SelectOptions {
	/** Force ctrl-style additive behavior — used by the checkbox button. */
	treatAsAdditive?: boolean;
}

interface SelectionAPI {
	handleSelect: (
		rowId: string,
		modifiers: ModifierFlags,
		opts?: SelectOptions,
	) => void;
}

const SelectionCtx = createContext<SelectionAPI | null>(null);

export function useSelectionAPI(): SelectionAPI {
	const v = useContext(SelectionCtx);
	if (!v)
		throw new Error("useSelectionAPI must be used within <SelectionProvider>");
	return v;
}

/**
 * Excel-style selection math. Given the visual row order, the current
 * selection, the anchor, and the click target + modifiers, return the next
 * selection and the next anchor.
 *
 *   - Plain click             → select only target; anchor = target
 *   - Ctrl/Cmd + click        → toggle target; anchor = target
 *   - Shift + click           → range [anchor..target] replaces selection; anchor unchanged
 *   - Ctrl/Cmd + Shift + click→ range [anchor..target] added to selection; anchor unchanged
 *
 * Anchor stays put on range gestures so consecutive shift-clicks feel like
 * an expanding/contracting range from the same pivot.
 */
export function computeNextSelection(
	orderedIds: readonly string[],
	current: RowSelectionState,
	anchor: string | null,
	targetId: string,
	flags: { additive: boolean; range: boolean },
): { next: RowSelectionState; nextAnchor: string | null } {
	const { additive, range } = flags;

	if (range) {
		const validAnchor = anchor && orderedIds.includes(anchor) ? anchor : null;
		if (!validAnchor) {
			// No usable anchor yet — degrade to a single toggle/select and seed it.
			const next: RowSelectionState = additive ? { ...current } : {};
			if (additive && next[targetId]) delete next[targetId];
			else next[targetId] = true;
			return { next, nextAnchor: targetId };
		}
		const a = orderedIds.indexOf(validAnchor);
		const b = orderedIds.indexOf(targetId);
		if (a === -1 || b === -1) return { next: current, nextAnchor: anchor };
		const [lo, hi] = a <= b ? [a, b] : [b, a];
		const rangeSel: RowSelectionState = {};
		for (let i = lo; i <= hi; i++) rangeSel[orderedIds[i]] = true;
		const next = additive ? { ...current, ...rangeSel } : rangeSel;
		return { next, nextAnchor: anchor };
	}

	if (additive) {
		const next = { ...current };
		if (next[targetId]) delete next[targetId];
		else next[targetId] = true;
		return { next, nextAnchor: targetId };
	}

	return { next: { [targetId]: true }, nextAnchor: targetId };
}

interface SelectionProviderProps<T> {
	orderedRows: readonly Row<T>[];
	selection: RowSelectionState;
	setSelection: OnChangeFn<RowSelectionState>;
	children: ReactNode;
}

export function SelectionProvider<T>({
	orderedRows,
	selection,
	setSelection,
	children,
}: SelectionProviderProps<T>) {
	const anchorRef = useRef<string | null>(null);

	// Refs let handleSelect stay stable while always reading the latest state,
	// so consumers (row div, checkbox button) don't re-bind handlers per render.
	const orderedIdsRef = useRef<string[]>([]);
	orderedIdsRef.current = orderedRows.map((r) => r.id);
	const selectionRef = useRef(selection);
	selectionRef.current = selection;

	const handleSelect = useCallback<SelectionAPI["handleSelect"]>(
		(rowId, { ctrlKey, metaKey, shiftKey }, opts) => {
			const additive = ctrlKey || metaKey || !!opts?.treatAsAdditive;
			const { next, nextAnchor } = computeNextSelection(
				orderedIdsRef.current,
				selectionRef.current,
				anchorRef.current,
				rowId,
				{ additive, range: shiftKey },
			);
			setSelection(next);
			anchorRef.current = nextAnchor;
		},
		[setSelection],
	);

	return (
		<SelectionCtx.Provider value={{ handleSelect }}>
			{children}
		</SelectionCtx.Provider>
	);
}
